import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import ToastContainer from '../common/ToastContainer';
import Button from '../common/Button';
import { useToast } from '../../hooks/useToast';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import LaboratoryOrderService from '../../services/laboratoryOrderService';
import {
  MANUAL_STATUS_OPTIONS,
  formatLaboratoryDate,
  getDisplayStatus
} from './laboratoryOrderUtils';
import LaboratoryOrdersView from './LaboratoryOrdersView';
import PatientSectionHeader from '../patients/common/PatientSectionHeader';
import '../patients/Patients.css';
import './LaboratoryOrders.css';

const LaboratoryOrders = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { timezone } = useOrganizationTimezone();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('open');
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingOrderId, setUploadingOrderId] = useState(null);
  const token = auth.user?.access_token;
  const patientFilterId = useMemo(() => new URLSearchParams(location.search).get('patientId') || '', [location.search]);
  // PatientDetails hands the name over in navigation state, but that is lost on
  // reload, so fall back to the name carried by the orders themselves (they are
  // already filtered down to this patient).
  const patientFilterName = useMemo(() => (
    location.state?.patientName
      || orders.find((order) => order.patientName)?.patientName
      || ''
  ), [location.state?.patientName, orders]);

  const replaceOrderInState = useCallback((updatedOrder) => {
    if (!updatedOrder?.id) {
      return;
    }

    setOrders((currentOrders) => currentOrders.map((item) => (
      item.id === updatedOrder.id ? { ...item, ...updatedOrder } : item
    )));
  }, []);

  const loadData = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      setIsLoading(true);
      const orderItems = await LaboratoryOrderService.getAllOrdersAllPages(token, 100, {
        patientId: patientFilterId || undefined
      });
      const sortedOrders = [...orderItems].sort((left, right) => new Date(right.createdAt) - new Date(left.createdAt));
      setOrders(sortedOrders);
    } catch (error) {
      showError('No se pudieron cargar las ordenes de laboratorio');
    } finally {
      setIsLoading(false);
    }
  }, [patientFilterId, showError, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (location.state?.message && location.state.messageId) {
      const storageKey = `laboratoryOrdersToast:${location.state.messageId}`;

      if (!sessionStorage.getItem(storageKey)) {
        showSuccess(location.state.message);
        sessionStorage.setItem(storageKey, 'shown');
      }

      // Keep location.search: dropping it here would silently clear the
      // patientId filter right after showing the toast.
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state?.message, location.state?.messageId, navigate, showSuccess]);

  const uploadFilesToOrder = useCallback(async (orderId, filesToUpload) => {
    if (!filesToUpload.length || !token) {
      return;
    }

    for (const file of filesToUpload) {
      const contentType = file.type || 'application/octet-stream';
      const uploadData = await LaboratoryOrderService.getAttachmentUploadUrl(
        orderId,
        { fileName: file.name, contentType },
        token
      );
      await LaboratoryOrderService.uploadAttachmentFile(uploadData.uploadUrl, file, contentType);
      await LaboratoryOrderService.attachFile(
        orderId,
        {
          attachmentId: uploadData.attachmentId,
          fileName: file.name,
          contentType,
          key: uploadData.key
        },
        token
      );
    }
  }, [token]);

  const handleOrderFileUpload = async (orderId, event) => {
    const filesToUpload = Array.from(event.target.files || []);
    event.target.value = '';

    if (!filesToUpload.length) {
      return;
    }

    try {
      setUploadingOrderId(orderId);
      await uploadFilesToOrder(orderId, filesToUpload);
      await loadData();
      showSuccess('Adjuntos cargados correctamente');
    } catch (error) {
      showError(error.message || 'No se pudieron cargar los adjuntos');
    } finally {
      setUploadingOrderId(null);
    }
  };

  const handleStatusChange = useCallback(async (orderId, nextStatus) => {
    const order = orders.find((item) => item.id === orderId);

    if (!order || !nextStatus || nextStatus === order.status) {
      return;
    }

    const previousOrder = { ...order };

    try {
      replaceOrderInState({ ...order, status: nextStatus });
      const updatedOrder = await LaboratoryOrderService.updateOrderStatus(
        orderId,
        {
          status: nextStatus,
          note: nextStatus === 'rework'
            ? 'Pieza marcada para ajuste'
            : nextStatus === 'cancelled'
              ? 'Orden cancelada desde el listado'
              : ''
        },
        token
      );
      replaceOrderInState(updatedOrder);
      showSuccess('Estado actualizado');
    } catch (error) {
      replaceOrderInState(previousOrder);
      showError(error.message || 'No se pudo actualizar el estado');
    }
  }, [orders, replaceOrderInState, showError, showSuccess, token]);

  const handleCancelOrder = async (order) => {
    if (order.status === 'cancelled') {
      return;
    }

    const confirmed = window.confirm(`¿Cancelar la orden de ${order.patientName}?`);
    if (!confirmed) {
      return;
    }

    await handleStatusChange(order.id, 'cancelled');
  };

  const completedOrders = useMemo(() => {
    return orders.filter((order) => {
      const displayStatus = getDisplayStatus(order, timezone);
      return displayStatus === 'received_in_clinic' || displayStatus === 'cancelled';
    });
  }, [orders, timezone]);

  const openOrders = useMemo(() => {
    return orders.filter((order) => {
      const displayStatus = getDisplayStatus(order, timezone);
      return displayStatus !== 'received_in_clinic' && displayStatus !== 'cancelled';
    });
  }, [orders, timezone]);

  const visibleOrders = activeTab === 'completed' ? completedOrders : openOrders;

  const filteredOrders = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return visibleOrders.filter((order) => {
      if (!normalizedSearch) {
        return true;
      }

      return [
        order.patientName,
        order.doctorName,
        order.providerName,
        order.workType
      ].some((value) => (value || '').toLowerCase().includes(normalizedSearch));
    });
  }, [searchTerm, visibleOrders]);

  const emptyTitle = searchTerm.trim()
    ? 'No se encontraron resultados'
    : activeTab === 'completed'
      ? 'No hay ordenes completadas'
      : 'No hay ordenes activas';

  const emptyActionLabel = activeTab === 'completed' ? 'Ver otras ordenes' : 'Ver todas las ordenes';

  const headerActions = (
    <>
      {patientFilterId ? (
        <Button
          variant="outline"
          size="lg"
          icon="fas fa-times"
          onClick={() => navigate('/laboratory-orders')}
        >
          Quitar filtro
        </Button>
      ) : null}
      <Button
        variant="primary"
        size="lg"
        icon="fas fa-plus"
        onClick={() => navigate(patientFilterId
          ? `/laboratory-orders/new?patientId=${patientFilterId}`
          : '/laboratory-orders/new')}
      >
        Nueva Orden
      </Button>
    </>
  );

  if (isLoading) {
    return (
      <div className="laboratory-orders-container laboratory-orders-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="laboratory-orders-container">
      {/* Reached from a patient: use the shared section header so the back
          button returns to that patient, like every other patient section. */}
      {patientFilterId ? (
        <PatientSectionHeader
          patientId={patientFilterId}
          patientName={patientFilterName || 'Paciente'}
          sectionTitle="Laboratorio Dental"
          actionButton={headerActions}
        />
      ) : (
        <div className="patients-header laboratory-orders-header">
          <div className="header-content">
            <h1>Laboratorio Dental</h1>
            <p>Administra las ordenes de laboratorio y separa las completadas del trabajo en curso.</p>
          </div>
          <div className="laboratory-orders-header-actions">
            {headerActions}
          </div>
        </div>
      )}

      <div className="patients-tabs laboratory-orders-tabs">
        <button
          className={`tab ${activeTab === 'open' ? 'active' : ''}`}
          onClick={() => setActiveTab('open')}
        >
          <i className="fas fa-flask"></i>
          Activas
          <span className="tab-count">{openOrders.length}</span>
        </button>
        <button
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <i className="fas fa-check-circle"></i>
          Completadas
          {(completedOrders.length > 0 || activeTab === 'completed') && (
            <span className="tab-count">{completedOrders.length}</span>
          )}
        </button>
      </div>

      <div className="search-section laboratory-orders-search-section">
        <div className={`search-bar ${searchTerm ? 'searching' : ''}`}>
          <input
            type="text"
            placeholder={patientFilterId
              ? 'Buscar ordenes por trabajo, doctor o proveedor...'
              : 'Buscar ordenes por paciente, trabajo, doctor o proveedor...'}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          {searchTerm ? (
            <Button
              variant="outline"
              icon="fas fa-times"
              onClick={() => setSearchTerm('')}
              title="Limpiar búsqueda"
            />
          ) : null}
        </div>
      </div>

      <div className="patients-content laboratory-orders-content">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-search"></i>
            <h3>{emptyTitle}</h3>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setActiveTab('open');
              }}
            >
              {emptyActionLabel}
            </Button>
          </div>
        ) : (
          <LaboratoryOrdersView
            orders={filteredOrders}
            timezone={timezone}
            manualStatusOptions={MANUAL_STATUS_OPTIONS}
            getDisplayStatus={getDisplayStatus}
            formatLaboratoryDate={formatLaboratoryDate}
            onEdit={(order) => navigate(`/laboratory-orders/${order.id}/edit`)}
            onStatusChange={handleStatusChange}
            onCancel={handleCancelOrder}
            onUpload={handleOrderFileUpload}
            uploadingOrderId={uploadingOrderId}
          />
        )}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default LaboratoryOrders;